import {
  inject,
  SimpleChanges,
  Component,
  output,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import type {
  ActivitySubscriptionType,
  ActivitySubscriptions,
  ActivitySubscriptionUpdateData,
} from '@core/activities-types';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { FeatureCode } from '@core/feature-spec-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { ReferralsService } from '@core/referrals-legacy';

import { ActivitySubscriptionsTableRowComponent } from '../activity-subscriptions-table/activity-subscriptions-table-row/activity-subscriptions-table-row.component';
import { ActivitySubscriptionsTableComponent } from '../activity-subscriptions-table/activity-subscriptions-table.component';

@Component({
  selector: 'rt-activity-subscriptions-company',
  templateUrl: './activity-subscriptions-company.component.html',
  styleUrls: ['../activity-subscriptions.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    ActivitySubscriptionsTableComponent,
    ActivitySubscriptionsTableRowComponent,
  ],
})
export class ActivitySubscriptionsCompanyComponent {
  private readonly featureSpecDataHelpers = inject(FeatureSpecFacade);
  private readonly referralsService = inject(ReferralsService);

  public readonly types = input<ActivitySubscriptionType[]>(undefined!);
  public readonly activitySubscriptions = input<ActivitySubscriptions>(undefined!);
  public readonly abilities = input({
    viewAllOffers: false,
    manageRecruiters: false,
    shareCandidates: false,
    manageCandidates: false,
    manageAttributes: false,
    manageDisqualifyReasons: false,
    manageReferrals: false,
    manageTexting: false,
    viewRequisitions: false,
    manageLocations: false,
    manageJobTemplates: false,
    manageIntegrations: false,
    viewCandidates: false,
  });
  public readonly hasRequisitions = input(false);
  public readonly isWhatsappEnabled = input(false);

  public changeEvents = output<ActivitySubscriptionUpdateData>();
  public canViewActivitySubscriptions = false;

  public hasReferrals: boolean = this.referralsService.hasReferralsAndReferralsFlag();
  protected hasJobTemplatesFeature = this.featureSpecDataHelpers.selectIsEnabled(
    FeatureCode.JobTemplates,
  );
  protected isScreeningAssistantEnabled = this.featureSpecDataHelpers.selectIsEnabled(
    FeatureCode.AiCandidateScreening,
  );
  protected isMatchingAssistantEnabled = this.featureSpecDataHelpers.selectIsEnabled(
    FeatureCode.AiCandidateMatching,
  );

  public ngOnChanges(changes: SimpleChanges): void {
    if ('abilities' in changes) {
      const { manageReferrals, manageJobTemplate, ...restAbilities } =
        changes.abilities.currentValue;
      this.canViewActivitySubscriptions =
        restAbilities.viewAllOffers ||
        restAbilities.manageRecruiters ||
        restAbilities.shareCandidates ||
        restAbilities.manageCandidates ||
        restAbilities.manageAttributes ||
        restAbilities.manageDisqualifyReasons ||
        restAbilities.viewRequisitions ||
        restAbilities.manageTexting ||
        restAbilities.manageLocations ||
        (this.hasReferrals && manageReferrals) ||
        (this.hasJobTemplatesFeature() && manageJobTemplate) ||
        (this.isScreeningAssistantEnabled() && restAbilities.viewCandidates) ||
        (this.isMatchingAssistantEnabled() && restAbilities.viewCandidates);
    }
  }
}
